import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import NotFound from '../ul/NotFound';

const generateChartData = (data) => {
    if (!data || data.length === 0) return null;

    const counts = data.reduce((acc, entry) => {
        const date = new Date(entry.createdAt);
        const day = date.toLocaleDateString('en-GB'); // Format: dd/MM/yyyy
        acc[day] = (acc[day] || 0) + entry.count;
        return acc;
    }, {});

    // Convert to array and sort by date
    const sortedData = Object.entries(counts)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => {
            const [dayA, monthA, yearA] = a.date.split('/');
            const [dayB, monthB, yearB] = b.date.split('/');
            return new Date(`${yearA}-${monthA}-${dayA}`) - new Date(`${yearB}-${monthB}-${dayB}`);
        });

    return sortedData;
};

const ChartComponent = ({ data, label, color }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        const chartData = generateChartData(data);
        if (!chartData) return;

        // Initialize chart
        if (chartRef.current) {
            // Dispose previous instance if it exists
            if (chartInstance.current) {
                chartInstance.current.dispose();
            }

            // Create new chart instance
            chartInstance.current = echarts.init(chartRef.current);

            // Prepare data for ECharts format
            const xAxisData = chartData.map(item => item.date);
            const seriesData = chartData.map(item => item.count);

            // Set chart options
            const option = {
                title: {
                    text: label,
                    left: 'center',
                    textStyle: {
                        color: '#111723',
                        fontWeight: 'bold',
                        fontSize: 16
                    }
                },
                tooltip: {
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: color,
                    borderWidth: 1,
                    textStyle: {
                        color: '#111723',
                        fontSize: 12
                    },
                    formatter: function (params) {
                        const dataIndex = params[0].dataIndex;
                        return `<div style="padding: 4px 8px;">
                            <div style="font-weight: bold; margin-bottom: 4px;">${xAxisData[dataIndex]}</div>
                            <div>
                                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 5px;"></span>
                                <span>${params[0].value} ${label}</span>
                            </div>
                        </div>`;
                    }
                },
                xAxis: {
                    type: 'category',
                    data: xAxisData,
                    boundaryGap: false,
                    axisLine: {
                        lineStyle: {
                            color: '#CFD1D3'
                        }
                    },
                    axisTick: {
                        show: false
                    },
                    axisLabel: {
                        color: '#888B91',
                        fontSize: 11
                    }
                },
                yAxis: {
                    type: 'value',
                    splitLine: {
                        lineStyle: {
                            color: '#F9FAFE',
                            type: 'dashed'
                        }
                    },
                    axisLine: {
                        show: false
                    },
                    axisTick: {
                        show: false
                    },
                    axisLabel: {
                        color: '#888B91',
                        fontSize: 11
                    }
                },
                series: [
                    {
                        data: seriesData,
                        type: 'line',
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 6,
                        lineStyle: {
                            color: color,
                            width: 3,
                            shadowColor: 'rgba(0, 0, 0, 0.1)',
                            shadowBlur: 4,
                            shadowOffsetY: 2
                        },
                        itemStyle: {
                            color: color,
                            borderWidth: 2,
                            borderColor: '#fff',
                            shadowColor: 'rgba(0, 0, 0, 0.1)',
                            shadowBlur: 2
                        },
                        areaStyle: {
                            color: {
                                type: 'linear',
                                x: 0,
                                y: 0,
                                x2: 0,
                                y2: 1,
                                colorStops: [
                                    {
                                        offset: 0,
                                        color: `${color}50` // 50% opacity
                                    },
                                    {
                                        offset: 1,
                                        color: `${color}10` // 10% opacity
                                    }
                                ]
                            }
                        }
                    }
                ],
                grid: {
                    left: '5%',
                    right: '5%',
                    bottom: '10%',
                    top: '15%',
                    containLabel: true
                }
            };

            // Apply options
            chartInstance.current.setOption(option);

            // Responsive handling
            const handleResize = () => {
                chartInstance.current.resize();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                if (chartInstance.current) {
                    chartInstance.current.dispose();
                }
            };
        }
    }, [data, label, color]);

    if (!data || data.length === 0) {
        return <NotFound />;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div ref={chartRef} style={{ width: '100%', height: '350px' }}></div>
        </div>
    );
};

const ViewChart = ({ ViewData }) => <ChartComponent data={ViewData} label="Views" color="#6849FF" />;
const ClickChart = ({ ClickData }) => <ChartComponent data={ClickData} label="Clicks" color="#FFC800" />;

export { ClickChart, ViewChart };